import { useReactiveVar } from '@apollo/client';
import { flashMessagesVar } from '../apollo/cache';
import { visuallyHiddenStyle } from '../hooks/useAriaAnnouncer';
import { SPACING } from '../styles/spacing';
import convertBbcode from '../utils/convertBbcode';
import Alert from './Alert';
import { PageContainer } from './layout/Grid';

type FlashMessagesProps = {
  attentionSeeking?: boolean; // if true (which is default), bring component into view when an error appears
};

export default function FlashMessages({ attentionSeeking = true }: FlashMessagesProps) {
  const flashMessages = useReactiveVar(flashMessagesVar);
  let ariaLiveSetting = 'polite';

  // when a "danger" flash message appears, scroll to it and make it assertive for screen readers
  if (Boolean(attentionSeeking) && flashMessages.some((fm) => fm.variant === 'danger')) {
    ariaLiveSetting = 'assertive';
    const messages = document.getElementById('flash-messages');
    const top = messages?.getBoundingClientRect().top ?? 0;
    window.scrollTo({ top: top + window.pageYOffset - parseInt(SPACING.xl), behavior: 'smooth' }); // scroll into view with an offset (nice)
  }

  return (
    <PageContainer
      data-testid="flash-messages"
      id="flash-messages"
      aria-live={ariaLiveSetting}
      aria-label="notifications"
      role="region"
    >
      {flashMessages.map((flashMessage) => (
        <>
          <Alert
            key={flashMessage.id}
            variant={flashMessage.variant}
            data-testid={flashMessage.variant}
          >
            <strong>
              {typeof flashMessage.text === 'string'
                ? convertBbcode(flashMessage.text)
                : flashMessage.text}
            </strong>
          </Alert>
          <div style={visuallyHiddenStyle}>
            {typeof flashMessage.text === 'string' &&
              flashMessage.text.includes('[link=') &&
              'A link within this message can be found in the notifications region.'}
          </div>
        </>
      ))}
    </PageContainer>
  );
}
